import React, { useState, useEffect } from "react";
import { Image } from "primereact/image";
import { Galleria } from "primereact/galleria";
import { getImageUrlFromCloudinary, getImagesByProductId } from "../../services/productsService";
import "../../pages/Product/Product.css";

/* ── Hook interno: detecta mobile ───────────────────────────── */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

export function ProductImages({ productId, onUrlEmpty = () => {} }) {
  const [url, setUrl] = useState(null);
  const [publicIds, setPublicIds] = useState(null);
  const [folderName, setFolderName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const isMobile = useIsMobile();

  /* ── Carga IDs de imágenes ──────────────────────────────── */
  useEffect(() => {
    if (!productId) {
      setPublicIds(null); setUrl(null); setFolderName(null); setErrorMessage(null);
      return;
    }
    setLoading(true);
    getImagesByProductId(productId)
      .then((images) => {
        if (images?.result?.length) {
          setPublicIds(JSON.parse(images.result[0].image_id));
          setFolderName(images.result[0].folder_name);
          setErrorMessage(null);
        } else {
          setPublicIds(null); setUrl(null); setFolderName(null);
        }
      })
      .catch((e) => {
        console.error(e);
        setErrorMessage(e.message || "Failed to load images");
        setPublicIds(null); setUrl(null); setFolderName(null);
      })
      .finally(() => setLoading(false));
  }, [productId]);

  /* ── Carga URLs de Cloudinary ───────────────────────────── */
  useEffect(() => {
    setLoading(true);
    if (publicIds && folderName) {
      getImageUrlFromCloudinary(folderName, publicIds)
        .then((imageUrl) => {
          setUrl(Array.isArray(imageUrl) ? imageUrl : [imageUrl]);
          setErrorMessage(null);
        })
        .catch((e) => {
          console.error(e);
          setErrorMessage(e.message || "Failed to load image URLs");
          setUrl(null);
        })
        .finally(() => setLoading(false));
    } else {
      setUrl(null);
      setLoading(false);
    }
  }, [publicIds, folderName]);

  /* ── Notifica al padre si no hay imágenes ───────────────── */
  useEffect(() => {
    const hasImages = Array.isArray(url) && url.length > 0;
    try {
      if (typeof onUrlEmpty === "function") onUrlEmpty(!hasImages);
    } catch (err) {
      console.error("onUrlEmpty callback error", err);
    }
  }, [url, onUrlEmpty]);

  /* ── Templates de Galleria ──────────────────────────────── */
  const itemTemplate = (item) => (
    <div className="gallery-item">
      <Image
        src={item}
        alt="Imagen del producto"
        style={{ display: "block", width: "100%" }}
        preview
      />
    </div>
  );

  const thumbnailTemplate = (item) => (
    <div className="gallery-thumbnail">
      <img src={item} alt="thumb" />
    </div>
  );

  /* ── Estados: loading / error / sin imágenes ────────────── */
  if (loading) {
    return (
      <div className="gallery-placeholder">
        <div className="gallery-placeholder__spinner" />
        <p className="gallery-placeholder__text">Cargando imágenes...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="gallery-placeholder gallery-placeholder--error">
        <i className="pi pi-exclamation-circle gallery-placeholder__icon" />
        <p className="gallery-placeholder__text">Error al cargar imágenes</p>
        <p className="gallery-placeholder__sub">{errorMessage}</p>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="gallery-placeholder">
        <i className="pi pi-image gallery-placeholder__icon" />
        <p className="gallery-placeholder__text">Sin imágenes disponibles</p>
        <p className="gallery-placeholder__sub">Puedes agregar imágenes a este producto</p>
      </div>
    );
  }

  return (
    <div className="gallery-wrap">
      <Galleria
        value={url}
        numVisible={isMobile ? 1 : 5}
        circular
        style={{ width: "100%" }}
        showItemNavigators
        showItemNavigatorsOnHover
        showIndicators={url.length > 1}
        showThumbnails={!isMobile && url.length > 1}
        item={itemTemplate}
        thumbnail={thumbnailTemplate}
        className="product-galleria"
      />
    </div>
  );
}
